import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

const RevenuePanel = ({ accounts = [] }) => {
  const stats = useMemo(() => {
    const planRevenue = { free: 0, professional: 49, enterprise: 199 };
    const activeAccounts = accounts?.filter(a => a?.status === 'active');
    const mrr = activeAccounts?.reduce((sum, a) => {
      const plan = a?.plan?.toLowerCase() || 'free';
      return sum + (planRevenue?.[plan] || 0);
    }, 0);

    const planCounts = accounts?.reduce((acc, a) => {
      const plan = a?.plan?.toLowerCase() || 'free';
      acc[plan] = (acc?.[plan] || 0) + 1;
      return acc;
    }, {});

    const planBreakdown = [
      { name: 'Free', count: planCounts?.free || 0, revenue: 0, color: '#94a3b8' },
      { name: 'Professional', count: planCounts?.professional || 0, revenue: (planCounts?.professional || 0) * 49, color: '#3b82f6' },
      { name: 'Enterprise', count: planCounts?.enterprise || 0, revenue: (planCounts?.enterprise || 0) * 199, color: '#8b5cf6' },
    ];

    // Simulated monthly revenue trend (last 6 months)
    const months = ['Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul'];
    const revenueTrend = months?.map((month, i) => ({
      month,
      revenue: Math.round(mrr * (0.6 + i * 0.08) + Math.random() * 200),
    }));
    revenueTrend[revenueTrend.length - 1].revenue = mrr;

    return { mrr, arr: mrr * 12, planBreakdown, revenueTrend, activeAccounts: activeAccounts?.length };
  }, [accounts]);

  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="p-5 border-b border-border">
        <h2 className="text-lg font-heading font-semibold text-foreground">Revenue Overview</h2>
        <p className="text-sm text-muted-foreground">Monthly recurring revenue and subscription breakdown</p>
      </div>

      <div className="p-5 space-y-6">
        {/* MRR / ARR */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-xs text-green-600 font-medium uppercase tracking-wide mb-1">MRR</p>
            <p className="text-2xl font-bold text-green-700">${stats?.mrr?.toLocaleString()}</p>
            <p className="text-xs text-green-600 mt-0.5">Monthly Recurring Revenue</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-xs text-blue-600 font-medium uppercase tracking-wide mb-1">ARR</p>
            <p className="text-2xl font-bold text-blue-700">${stats?.arr?.toLocaleString()}</p>
            <p className="text-xs text-blue-600 mt-0.5">Annual Recurring Revenue</p>
          </div>
        </div>

        {/* Revenue Trend Chart */}
        {accounts?.length > 0 && (
          <div>
            <p className="text-sm font-medium text-foreground mb-3">Revenue Trend (6 months)</p>
            <ResponsiveContainer width="100%" height={140}>
              <LineChart data={stats?.revenueTrend} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} />
                <Tooltip formatter={(v) => [`$${v?.toLocaleString()}`, 'Revenue']} contentStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="revenue" stroke="#22c55e" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Plan Breakdown */}
        <div>
          <p className="text-sm font-medium text-foreground mb-3">Revenue by Plan</p>
          <div className="space-y-2.5">
            {stats?.planBreakdown?.map((plan) => (
              <div key={plan?.name} className="flex items-center gap-3">
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: plan?.color }} />
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-foreground">{plan?.name}</span>
                    <div className="flex items-center gap-3 text-sm">
                      <span className="text-muted-foreground">{plan?.count} accounts</span>
                      <span className="font-medium text-foreground">${plan?.revenue?.toLocaleString()}/mo</span>
                    </div>
                  </div>
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: stats?.mrr > 0 ? `${(plan?.revenue / stats?.mrr) * 100}%` : plan?.count > 0 ? '20%' : '0%',
                        backgroundColor: plan?.color
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Account Counts by Plan Bar Chart */}
        {accounts?.length > 0 && (
          <div>
            <p className="text-sm font-medium text-foreground mb-3">Accounts by Plan</p>
            <ResponsiveContainer width="100%" height={100}>
              <BarChart data={stats?.planBreakdown} margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Bar dataKey="count" name="Accounts" fill="#6366f1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {accounts?.length === 0 && (
          <div className="text-center py-6 text-muted-foreground">
            <Icon name="BarChart3" size={32} className="mx-auto mb-2 opacity-40" />
            <p className="text-sm">Revenue data will appear once accounts are loaded</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RevenuePanel;
